/**
 * LiveVisitorPopup — slack-mention-style attention grabber that surfaces a
 * new live visitor on the merchant's storefront from anywhere in the
 * dashboard. Mounted globally in Shell.js.
 *
 * Behaviour (per-visitor retry):
 *   1. `visitorActive` socket event on /storefront-chatbot fires.
 *   2. Popup shows for AUTO_DISMISS_MS, then disappears.
 *   3. If the merchant didn't click, schedule a retry RETRY_GAP_MS later.
 *   4. Cap at MAX_APPEARANCES total per visitor. After that, that visitor
 *      stays quiet until the page is reloaded.
 *
 * Self-suppress when:
 *   - merchant lacks the `sales.storefront_chatbot` feature
 *   - merchant is already on /storefront-chat/live (they can see it)
 *   - merchant is inside a specific conversation /storefront-chat/:id
 *   - the visitor flips to idle (`visitorIdle` event) before retries fire
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io as socketIo } from 'socket.io-client';
import { Eye, X } from 'lucide-react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import './LiveVisitorPopup.css';

const MAX_APPEARANCES = 3;
const AUTO_DISMISS_MS = 6000;
const RETRY_GAP_MS = 20000;

function getSocketUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4000';
  }
  return 'https://revstar-main-platform-production.up.railway.app';
}

// Merchant auth is HTTP-only cookie (`revstar_merchant_token`) — JS can't
// read it, so we just enable withCredentials and let the browser send it on
// the websocket upgrade. The backend reads cookies in the chatbot
// namespace auth middleware.

const LiveVisitorPopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = useFeatureAccess('sales.storefront_chatbot');

  // Currently displayed popup payload, or null.
  const [current, setCurrent] = useState(null);

  // Per-visitor appearance counter. Map<visitorId, number>.
  const seenRef = useRef(new Map());
  // Pending visitor payloads (FIFO).
  const queueRef = useRef([]);
  // Active retry timers (Map<visitorId, timeoutId>) — used so we can cancel
  // when the visitor flips to idle.
  const retryTimersRef = useRef(new Map());
  // Auto-dismiss timer for the currently displayed popup.
  const dismissTimerRef = useRef(null);

  // Suppress on routes that already display the live activity.
  const onLivePage = location.pathname === '/storefront-chat/live';
  const onConvPage = /^\/storefront-chat\/[^/]+$/.test(location.pathname)
    && !location.pathname.endsWith('/settings')
    && !location.pathname.endsWith('/live');
  const suppressed = onLivePage || onConvPage;

  const showNext = useCallback(() => {
    if (suppressed) return;
    setCurrent((prev) => {
      if (prev) return prev; // one at a time
      const next = queueRef.current.shift();
      if (!next) return null;

      const count = (seenRef.current.get(next.visitorId) || 0) + 1;
      seenRef.current.set(next.visitorId, count);

      // Schedule auto-dismiss
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setCurrent(null);

        // If still under retry cap, schedule another appearance
        if (count < MAX_APPEARANCES) {
          const t = setTimeout(() => {
            // Only re-enqueue if visitor isn't muted (idle event clears the
            // retry timer + bumps the counter to MAX).
            const stillUnderCap = (seenRef.current.get(next.visitorId) || 0) < MAX_APPEARANCES;
            if (stillUnderCap) {
              queueRef.current.push(next);
              showNext();
            }
            retryTimersRef.current.delete(next.visitorId);
          }, RETRY_GAP_MS);
          retryTimersRef.current.set(next.visitorId, t);
        }
      }, AUTO_DISMISS_MS);

      return { ...next, appearance: count };
    });
  }, [suppressed]);

  // Open socket only when the feature is on and the user is on a dashboard
  // route. Reuses the existing /storefront-chatbot namespace + merchant
  // auth path that powers the inbox + live page.
  useEffect(() => {
    if (!hasAccess) {
      console.log('[LiveVisitorPopup] gated: no feature access');
      return undefined;
    }

    console.log('[LiveVisitorPopup] opening socket to', getSocketUrl(), '/storefront-chatbot');
    const sock = socketIo(`${getSocketUrl()}/storefront-chatbot`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    sock.on('connect', () => console.log('[LiveVisitorPopup] socket connected', sock.id));
    sock.on('disconnect', (reason) => console.log('[LiveVisitorPopup] socket disconnected', reason));

    const onVisitorActive = (payload) => {
      console.log('[LiveVisitorPopup] visitor_active payload', payload);
      const v = payload?.visitor;
      if (!v?.id) { console.warn('[LiveVisitorPopup] dropped — no visitor.id'); return; }
      if ((seenRef.current.get(v.id) || 0) >= MAX_APPEARANCES) {
        console.log('[LiveVisitorPopup] dropped — at cap for', v.id);
        return;
      }
      if (queueRef.current.some((q) => q.visitorId === v.id)) {
        console.log('[LiveVisitorPopup] dropped — already queued', v.id);
        return;
      }
      queueRef.current.push({
        visitorId: v.id,
        displayName: v.displayName || v.name || 'A visitor',
        country: v.country || null,
        lastPagePath: v.lastPagePath || null,
      });
      console.log('[LiveVisitorPopup] enqueued, queue size now', queueRef.current.length);
      showNext();
    };

    const onVisitorIdle = ({ visitorId }) => {
      if (!visitorId) return;
      const t = retryTimersRef.current.get(visitorId);
      if (t) { clearTimeout(t); retryTimersRef.current.delete(visitorId); }
      seenRef.current.set(visitorId, MAX_APPEARANCES);
    };

    sock.on('live_activity:visitor_active', onVisitorActive);
    sock.on('live_activity:visitor_idle', onVisitorIdle);
    sock.on('connect_error', (err) => {
      console.warn('[LiveVisitorPopup] socket connect_error:', err?.message);
    });

    return () => {
      sock.off('live_activity:visitor_active', onVisitorActive);
      sock.off('live_activity:visitor_idle', onVisitorIdle);
      sock.disconnect();
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      retryTimersRef.current.forEach((t) => clearTimeout(t));
      retryTimersRef.current.clear();
    };
  }, [hasAccess, showNext]);

  // If we land on a suppressed route while a popup is up, hide it.
  useEffect(() => {
    if (suppressed && current) setCurrent(null);
  }, [suppressed, current]);

  const handleView = () => {
    if (!current) return;
    // Mute further retries for this visitor — they've been seen.
    seenRef.current.set(current.visitorId, MAX_APPEARANCES);
    const t = retryTimersRef.current.get(current.visitorId);
    if (t) { clearTimeout(t); retryTimersRef.current.delete(current.visitorId); }
    setCurrent(null);
    navigate('/storefront-chat/live');
  };

  const handleDismiss = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setCurrent(null);
    // Don't mute — let the natural retry happen if under cap.
    // showNext will pick the next queued one (if any).
    setTimeout(showNext, 50);
  };

  if (!current || suppressed) return null;

  return (
    <div className="lvp" role="status" aria-live="polite">
      <span className="lvp__pulse" aria-hidden>
        <span className="lvp__pulse-dot" />
      </span>
      <div className="lvp__body">
        <p className="lvp__title">{current.displayName} is on your store</p>
        <p className="lvp__sub">
          {current.lastPagePath || 'Browsing now'}
          {current.country ? ` · ${current.country}` : ''}
        </p>
      </div>
      <button type="button" className="lvp__cta" onClick={handleView}>
        <Eye size={13} /> View live
      </button>
      <button
        type="button"
        className="lvp__close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default LiveVisitorPopup;
